import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Image,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { imageService } from '../services/images';
import { rateLimitService } from '../services/rateLimit';
import { Colors, Fonts, Radii, Shadows, Typography } from '../constants';

interface ImageUploaderProps {
  maxImages?: number;
  onImagesUploaded: (publicIds: string[]) => void;
  initialImages?: string[];
}

export const ImageUploader: React.FC<ImageUploaderProps> = React.memo(({
  maxImages = 2,
  onImagesUploaded,
  initialImages = [],
}) => {
  const { t } = useTranslation();
  const [imagePublicIds, setImagePublicIds] = useState<string[]>(initialImages);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handlePickImages = async () => {
    try {
      setError(null);

      // Check if user can upload more images
      const remainingSlots = maxImages - imagePublicIds.length;
      if (remainingSlots <= 0) {
        setError(t('common.validation.maxImagesReached'));
        return;
      }

      // Check rate limit
      const canUpload = await rateLimitService.checkUploadLimit();
      if (!canUpload) {
        setError(t('common.validation.rateLimitReached'));
        return;
      }

      // Pick images
      const result = await imageService.pickImages(remainingSlots);

      if (result.canceled) {
        return;
      }

      // Upload images
      setUploading(true);
      setUploadProgress(0);

      const uris = result.assets.map((asset) => asset.uri);
      console.log('Uploading images from URIs:', uris);

      const publicIds = await imageService.uploadMultipleImages(uris, (progress) => {
        setUploadProgress(progress.percentage);
      });

      console.log('Upload successful, received publicIds:', publicIds);

      // Record upload in rate limit
      await rateLimitService.recordUpload();

      // Update state
      const newImagePublicIds = [...imagePublicIds, ...publicIds];
      setImagePublicIds(newImagePublicIds);
      onImagesUploaded(newImagePublicIds);

      console.log('Image state updated. Total images:', newImagePublicIds.length);

      setUploading(false);
      setUploadProgress(0);
    } catch (err: any) {
      console.error('Image upload error:', err);
      setError(err.message || t('common.validation.uploadFailed'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImagePublicIds = imagePublicIds.filter((_, i) => i !== index);
    setImagePublicIds(newImagePublicIds);
    onImagesUploaded(newImagePublicIds);
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('components.imageUploader.title')}</Text>
        <Text style={styles.hint}>
          {t('components.imageUploader.imagesCount', {
            current: imagePublicIds.length,
            max: maxImages,
          })}
        </Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.scrollView}>
        <View style={styles.imagesGrid}>
          {/* Display uploaded images */}
          {useMemo(() => imagePublicIds.map((publicId, index) => {
            const imageUrl = imageService.getThumbnailUrl(publicId);
            return (
              <View key={`${publicId}-${index}`} style={styles.imageContainer}>
                <Image
                  source={{ uri: imageUrl }}
                  style={styles.image}
                />
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => handleRemoveImage(index)}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.remove', { defaultValue: 'Remove' })}
                >
                  <Ionicons name="close" size={15} color={Colors.white} />
                </TouchableOpacity>
              </View>
            );
          }), [imagePublicIds])}

          {/* Add button */}
          {imagePublicIds.length < maxImages && !uploading && (
            <Pressable
              style={({ hovered }: any) => [styles.addButton, hovered && styles.addButtonHovered]}
              onPress={handlePickImages}
              accessibilityRole="button"
            >
              {({ hovered }: any) => (
                <>
                  <Ionicons
                    name="camera-outline"
                    size={28}
                    color={hovered ? Colors.accentStrong : Colors.textMedium}
                  />
                  <Text style={[styles.addButtonLabel, hovered && styles.addButtonLabelHovered]}>
                    {t('components.imageUploader.addPhoto')}
                  </Text>
                </>
              )}
            </Pressable>
          )}

          {/* Upload progress indicator */}
          {uploading && (
            <View style={styles.uploadingContainer}>
              <ActivityIndicator size="small" color={Colors.primary} />
              <Text style={styles.uploadingText}>
                {t('components.imageUploader.uploading')}
              </Text>
              <Text style={styles.progressText}>{Math.round(uploadProgress)}%</Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Error message, or the cover-photo hint */}
      {error ? (
        <Text style={styles.errorText}>{error}</Text>
      ) : (
        <Text style={styles.hintText}>{t('components.imageUploader.hint')}</Text>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 10,
  },
  title: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textDark,
  },
  hint: {
    ...Typography.overline,
    color: Colors.textLight,
  },
  scrollView: {
    marginBottom: 8,
  },
  imagesGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  imageContainer: {
    width: 120,
    height: 120,
    borderRadius: Radii.control,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: Colors.card,
    ...Shadows.card,
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 26,
    height: 26,
    borderRadius: Radii.thumb,
    backgroundColor: 'rgba(30, 59, 49, 0.75)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    width: 120,
    height: 120,
    borderRadius: Radii.control,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: Colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: Colors.background,
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  addButtonHovered: {
    borderColor: Colors.primary,
    backgroundColor: Colors.primaryLight,
  },
  addButtonLabel: {
    fontFamily: Fonts.medium,
    fontSize: 12,
    color: Colors.textMedium,
  },
  addButtonLabelHovered: {
    color: Colors.accentStrong,
  },
  uploadingContainer: {
    width: 120,
    height: 120,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Colors.background,
  },
  uploadingText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
  },
  progressText: {
    fontFamily: Fonts.bold,
    fontSize: 14,
    color: Colors.accentStrong,
  },
  errorText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.error,
    marginTop: 6,
  },
  hintText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
    marginTop: 6,
  },
});
